import React, { useState } from "react";
import { Twitter, Facebook, Linkedin, Mail, Link2, Check } from "lucide-react";
import { trackEvent } from "@/lib/observability";

const WhatsAppIcon: React.FC<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }> = ({
  className = "w-4 h-4",
  "aria-hidden": ariaHidden,
}) => (
  <svg
    className={className}
    aria-hidden={ariaHidden}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.447-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421-9.091a5.94 5.94 0 0 0-5.934 5.934 5.91 5.91 0 0 0 1.015 3.321l-1.075 3.916 4.01-1.052a5.94 5.94 0 0 0 2.984.802 5.94 5.94 0 0 0 5.934-5.935 5.94 5.94 0 0 0-5.934-5.986m0 10.97a5.03 5.03 0 0 1-2.566-.706l-.184-.11-2.492.654.664-2.424-.175-.184a5.03 5.03 0 0 1-1.337-3.415 5.03 5.03 0 0 1 5.025-5.025 5.03 5.03 0 0 1 5.025 5.025 5.03 5.03 0 0 1-5.025 5.185" />
  </svg>
);

type SharePlatform = "twitter" | "facebook" | "linkedin" | "whatsapp" | "email" | "copy_link";

const normalizeSlug = (slug: string) => slug.replace(/^\//, "");


interface ShareButtonsProps {
  /** Path from the site root, e.g. "ms-fatigue-tracker" or "blog/pillar/ms-fatigue". */
  slug: string;
  title: string;
  /** Optional short blurb used in email/twitter share text. */
  summary?: string;
  className?: string;
}

const SITE = "https://myelinahealth.com";

const ShareButtons: React.FC<ShareButtonsProps> = ({ slug, title, summary, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const baseUrl = `${SITE}/${slug.replace(/^\//, "")}`;
  const text = summary ? `${title}. ${summary}` : title;

  const buildShareUrl = (platform: SharePlatform) => {
    const u = new URL(baseUrl);
    u.searchParams.set("utm_source", platform);
    u.searchParams.set("utm_medium", "social_share");
    u.searchParams.set("utm_campaign", "pillar_referral");
    u.searchParams.set("utm_content", normalizeSlug(slug));
    return u.toString();
  };

  const links: Array<{ label: string; icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>; href: string; platform: SharePlatform }> = [
    {
      label: "Share on X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(buildShareUrl("twitter"))}`,
      platform: "twitter",
    },
    {
      label: "Share on Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildShareUrl("facebook"))}`,
      platform: "facebook",
    },
    {
      label: "Share on LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildShareUrl("linkedin"))}`,
      platform: "linkedin",
    },
    {
      label: "Share on WhatsApp",
      icon: WhatsAppIcon,
      href: `https://wa.me/?text=${encodeURIComponent(`${text}\n\n${buildShareUrl("whatsapp")}`)}`,
      platform: "whatsapp",
    },
    {
      label: "Share by email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${buildShareUrl("email")}`)}`,
      platform: "email",
    },
  ];

  const trackShare = (platform: SharePlatform) => {
    trackEvent("article_share_click", {
      platform,
      slug: normalizeSlug(slug),
      url: buildShareUrl(platform),
      title,
    });
  };

  const handleCopy = async () => {
    trackShare("copy_link");
    try {
      await navigator.clipboard.writeText(buildShareUrl("copy_link"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable, silently ignore.
    }
  };


  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      role="group"
      aria-label="Share this article"
    >
      <span className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mr-1">
        Share
      </span>
      {links.map(({ label, icon: Icon, href, platform }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          onClick={() => trackShare(platform)}
          className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-border bg-surface text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
        >
          <Icon className="w-4 h-4" aria-hidden="true" />
        </a>
      ))}

      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Link copied" : "Copy link"}
        className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-border bg-surface text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
      >
        {copied ? (
          <Check className="w-4 h-4 text-primary" aria-hidden="true" />
        ) : (
          <Link2 className="w-4 h-4" aria-hidden="true" />
        )}
      </button>
      <span aria-live="polite" className="sr-only">
        {copied ? "Link copied to clipboard" : ""}
      </span>
    </div>
  );
};

export default ShareButtons;
